import _ from 'underscore';
import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Dropdown, TextField, Link } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';

import { getStyles } from './get-styles';

// valid characters for URL
const urlPattern = /^[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;

const URL_MAX_LENGTH = 1024;

export const DestinationUrlAction = (props) => {
  const {
    goals,
    getValueErrorMessage,
    url,
    onURLChange,
    onNotifyValidationResult,
    expressionOperator,
    onExpressionOperatorChange,
    urlExpressionOperators,
  } = props;
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);
  const [isInitialized, setIsInitialized] = useState(!_.isEmpty(url));

  const duplicateUrlErrorText = useMemo(() => i18n.getString(_TL_('Goal with this url and operator already exists')), [i18n]);

  const checkDuplicateAction = useCallback((inputUrl, operator) => {
    const getGoalByUrlAndExpressionOperator = _.find(goals, g => g.url === inputUrl && g.expressionOperator === operator);

    if (getGoalByUrlAndExpressionOperator) {
      return duplicateUrlErrorText;
    }
    return '';
  }, [duplicateUrlErrorText, goals]);

  const urlErrorMessage = useMemo(() => {
    let errorMessage = '';

    if (isInitialized) {
      errorMessage = getValueErrorMessage(i18n, URL_MAX_LENGTH, url);

      // check for invalid characters
      if (_.isEmpty(errorMessage) && !urlPattern.test(url)) {
        errorMessage = i18n.getString(_TL_('Destination URL contains invalid characters'));
      }

      // check for duplicates
      if (_.isEmpty(errorMessage)) {
        errorMessage = checkDuplicateAction(url, expressionOperator);
      }
    }

    return errorMessage;
  }, [checkDuplicateAction, expressionOperator, getValueErrorMessage, i18n, isInitialized, url]);

  useEffect(() => onNotifyValidationResult(urlErrorMessage), [urlErrorMessage, onNotifyValidationResult]);

  const validateInput = useCallback((value) => {
    const duplicatedActionErrorMessage = checkDuplicateAction(value, expressionOperator);

    if (duplicatedActionErrorMessage) {
      return duplicatedActionErrorMessage;
    }

    const errorValidationOnParent = getValueErrorMessage(i18n, 1024, value);
    return errorValidationOnParent;
  }, [checkDuplicateAction, expressionOperator, getValueErrorMessage, i18n]);

  const onTextChange = useCallback((value) => {
    setIsInitialized(true);
    validateInput(value);
    onURLChange(value);
  }, [onURLChange, validateInput]);

  const onOperatorChange = useCallback((_event, option) => {
    onExpressionOperatorChange(option);
  }, [onExpressionOperatorChange]);

  const getDestinationUrlInputLabel = () => {
    switch (expressionOperator) {
      case 'EqualsTo':
        return i18n.getString(_TL_('Website URL'));
      case 'Contains':
        return i18n.getString(_TL_('String present in URL'));
      default:
        return undefined;
    }
  };

  const selectedExpressionOperatorOption = useMemo(() => (
    _.find(urlExpressionOperators, option => option.key === expressionOperator)
  ), [expressionOperator, urlExpressionOperators]);

  return (
    <div className="goal-action-form">
      <div className={classNames(classes.actionContainer, 'action-container')}>
        <Dropdown
          className={classNames('action-operator', classes.actionOperator)}
          selectedKey={expressionOperator}
          options={urlExpressionOperators}
          label={i18n.getString(_TL_('Destination URL'))}
          onChange={onOperatorChange}
          ariaLabel={i18n.getString(_TL_('Select action operator'))}
        />
        <TextField
          className={getDestinationUrlInputLabel() ? 'url-input' : classNames('url-input', classes.urlInput)}
          ariaLabel={i18n.getString(_TL_('Website URL'))}
          value={url}
          label={getDestinationUrlInputLabel()}
          onChange={({ target }) => onTextChange((target || {}).value)}
          errorMessage={urlErrorMessage}
        />
      </div>
      {/* operator explanation */}
      {!_.isEmpty(selectedExpressionOperatorOption) && (
        <div className={classNames(classes.operatorDescription, 'operator-description')}>
          <span>
            {selectedExpressionOperatorOption.description}
          </span>
          {selectedExpressionOperatorOption.helpLink && (
            <Link
              href={selectedExpressionOperatorOption.helpLink}
              className={classes.helpLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {i18n.getString(_TL_('Learn more'))}
            </Link>
          )}
        </div>
      )}
    </div>);
};

DestinationUrlAction.propTypes = {
  goals: PropTypes.arrayOf(PropTypes.shape({
    expressionOperator: PropTypes.string,
    url: PropTypes.string,
  })),
  getValueErrorMessage: PropTypes.func.isRequired,
  url: PropTypes.string,
  onURLChange: PropTypes.func.isRequired,
  onNotifyValidationResult: PropTypes.func,
  expressionOperator: PropTypes.oneOf(['EqualsTo', 'StartsWith', 'Contains', 'RegularExpression']).isRequired,
  onExpressionOperatorChange: PropTypes.func.isRequired,
  urlExpressionOperators: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    text: PropTypes.string,
    inputLabel: PropTypes.string,
    description: PropTypes.string,
    helpLink: PropTypes.string,
  })).isRequired,
};

DestinationUrlAction.defaultProps = {
  goals: undefined,
  onNotifyValidationResult: null,
  url: '',
};
